import { useCallback } from "react";
import { ComplianceProgramCalibrationGroupFragment } from "types/graphql-schema";

import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import { useDeleteCalibrationGroup } from "./hooks";

const CalibrationGroupsList = ({
  calibrationGroups,
  onClose,
  onEdit,
}: {
  calibrationGroups: ComplianceProgramCalibrationGroupFragment[];
  onClose: () => void;
  onEdit: (groupId: number) => void;
}) => {
  const {
    trashCalibrationGroup,
    isDeleting,
    DeleteConfirmationDialog,
    confirmDelete,
  } = useDeleteCalibrationGroup();

  const handleDeleteGroup = useCallback(
    async (groupId: number) => {
      const confirmation = await confirmDelete();
      if (confirmation) {
        trashCalibrationGroup({
          variables: {
            id: groupId,
          },
          onCompleted: () => {
            successNotificationVar({
              title: "Calibration Group Deleted",
            });
          },
        });
      }
    },
    [confirmDelete, trashCalibrationGroup],
  );

  return (
    <>
      <DeleteConfirmationDialog />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="tf-header-xl">Calibration Groups</div>
          {isDeleting && <Loading mini />}
        </div>
        <Button
          onClick={onClose}
          theme={buttonTheme.iconGray}
          icon={TopicflowIconMap.close}
        />
      </div>
      <div className="mt-4">
        {calibrationGroups.length === 0 ? (
          <div className="text-sm text-gray-500">No calibration groups</div>
        ) : (
          <div className="border rounded-md flex flex-col divide-y">
            {calibrationGroups.map((calibrationGroup) => (
              <div
                key={calibrationGroup.id}
                className="px-6 py-3.5 flex items-center justify-between"
              >
                <div className="text-sm">{calibrationGroup.name}</div>
                <div className="flex items-center">
                  <Button
                    disabled={isDeleting}
                    theme={buttonTheme.iconGray}
                    icon={TopicflowIconMap.edit}
                    onClick={() => onEdit(calibrationGroup.id)}
                  />
                  <Button
                    disabled={isDeleting}
                    theme={buttonTheme.iconGray}
                    icon={TopicflowIconMap.trash}
                    onClick={() => handleDeleteGroup(calibrationGroup.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CalibrationGroupsList;
